import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  CellModel, getCell, getCellIndexes, getColumnHeaderText, SheetModel, SpreadsheetComponent
} from '@syncfusion/ej2-angular-spreadsheet';
import {ClickEventArgs} from '@syncfusion/ej2-navigations';
import {L10n, loadCldr, setCulture, setCurrencyCode} from '@syncfusion/ej2-base';
import * as EJ2_LOCALE from '../../../../node_modules/@syncfusion/ej2-locale/src/de.json';

import deCurrencyData from '../../../../node_modules/cldr-data/main/de/currencies.json';
import deTimeZoneData from '../../../../node_modules/cldr-data/main/de/timeZoneNames.json';

import deNumberData from '../../../../node_modules/cldr-data/main/de/numbers.json';
import deCaGregorian from '../../../../node_modules/cldr-data/main/de/ca-gregorian.json';
import supplementalCurrencyData from '../../../../node_modules/cldr-data/supplemental/currencyData.json';
import supplementalNumberSystemData from '../../../../node_modules/cldr-data/supplemental/numberingSystems.json';

import {environment} from '../../../environments/environment';
import {ExgModalService} from 'exc-controls-v2';
import {ExcSecurityService} from 'exc-security-v2';

@Component({
  selector: 'app-calculation-spreadsheet',
  templateUrl: './calculation-spreadsheet.component.html',
  styles: []
})
export class CalculationSpreadsheetComponent implements OnInit, AfterViewInit {

  @ViewChild('spreadsheet') public spreadsheetObj!: SpreadsheetComponent;
  @Input() exceljson: any | undefined;
  @Input() autoFillObject: any | undefined;
  @Input() contextMenueItems: any[] | undefined;
  @Input() allowOpen = false;
  @Input() readOnly = false;
  @Input() isMGMT = false;
  @Input() isTemplate = false;
  @Input() calcId: string | undefined;

  @Output() Created: EventEmitter<any> = new EventEmitter<any>();
  @Output() Save: EventEmitter<any> = new EventEmitter<any>();
  @Output() autoFillColumns: EventEmitter<any> = new EventEmitter<any>();
  @Output() exportSheets: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadSheets: EventEmitter<any> = new EventEmitter<any>();

  ID: string | undefined = undefined;

  service = environment.rest.SpreadsheetUrl;

  selectedRowIndex: number | null = null;

  public isInsertingRow = false;
  public data?: any = undefined;
  public Names: any[] = [];
  public lastAction: {
    type: 'insert' | 'delete';
    rowIndex: number;
    cells: any[];
    height: number | undefined
  } | null = null;
  isDirty = false;

  constructor(
    private modal: ExgModalService,
    private excSecurity: ExcSecurityService
  ) {
  }

  ngOnInit(): void {
    L10n.load({de: EJ2_LOCALE.de});
    setCulture('de');
    setCurrencyCode('EUR');
    loadCldr(deCurrencyData, deTimeZoneData, deNumberData, deCaGregorian, supplementalCurrencyData, supplementalNumberSystemData);
  }

  ngAfterViewInit(): void {
    this.spreadsheetObj.element.addEventListener('paste', this.onNativePaste, true);
  }

  created(): void {
    if (!this.isMGMT) {
      this.spreadsheetObj.hideRibbonTabs(['Einfügung', 'Formeln', 'Daten', 'Sicht']);
    }

    if (!this.isTemplate) {
      this.spreadsheetObj.addRibbonTabs([
        {
          header: {text: 'ex:act'},
          content: [
            {
              text: 'Blatt export',
              prefixIcon: 'e-export e-icons',
              disabled: !(this.calcId && this.excSecurity.isFeatured('RCALCEXPORT')),
              click: (args: ClickEventArgs): void => {
                const sheets = [] as any[];

                for (const sheet of this.spreadsheetObj.sheets) {
                  if (sheet.state === 'Visible') {
                    sheets.push({sheetID: sheet.id, sheetName: sheet.name, sheetSelected: false, sheetFolder: 50});
                  }
                }

                this.modal.show(
                  'ModalCalculationVersionExportComponent',
                  {sheets, showFolderSelection: true}
                ).subscribe((retVal: any) => {
                  this.exportSheets.emit(retVal);
                });
              }
            },
            {
              text: 'Download PDFs',
              prefixIcon: 'e-save e-icons',
              disabled: !(this.calcId && this.excSecurity.isFeatured('RCALCDOWNLOAD')),
              click: (args: ClickEventArgs): void => {
                const sheets = [] as any[];

                for (const sheet of this.spreadsheetObj.sheets) {
                  if (sheet.state === 'Visible') {
                    sheets.push({sheetID: sheet.id, sheetName: sheet.name, sheetSelected: false, sheetFolder: -1});
                  }
                }

                this.modal.show(
                  'ModalCalculationVersionExportComponent',
                  {sheets, showFolderSelection: false}
                ).subscribe((retVal: any) => {
                  this.downloadSheets.emit(retVal);
                });
              }
            },
            ...(!this.readOnly ? [
              {
                text: 'Zeile hinzufügen',
                prefixIcon: 'e-add-below e-icons',
                click: (): void => { this.insertRowBelowSelected(); }
              },
              {
                text: 'Zeile entfernen',
                prefixIcon: 'e-trash e-icons',
                click: (): void => { this.removeSelectedRow(); }
              },
              {
                text: 'Rückgängig',
                prefixIcon: 'e-undo e-icons',
                click: (): void => { this.undoLastAction(); }
              }
            ] : [])
          ]
        }]);
    }

    if (this.exceljson) {
      this.spreadsheetObj.openFromJson({file: this.exceljson, triggerEvent: false});
      if (this.autoFillObject) {
        this.doAutoFill();
      }
      this.autoFillColumns.emit(this.getAutoFillColumns());
      // Autorepair Color Format
      this.normalizeSpreadsheetColors().then();
      this.normalizeSpreadsheetFontSizes().then();
    }
  }

  onCellSave(): void {
    this.isDirty = true;
  }

  onActionComplete(): void {
    this.isDirty = true;
  }

  doAutoFill(): void {
    const rx = /{{\s*([^}]+?)\s*}}/g;

    for (const sheet of this.spreadsheetObj.sheets) {
      if (!sheet?.rows) {
        continue;
      }

      for (const row of sheet.rows) {
        if (!row?.cells) {
          continue;
        }

        for (const cell of row.cells) {
          const cellValue = cell?.value;
          if (typeof cellValue !== 'string' || cellValue.length === 0) {
            continue;
          }
          if (!rx.test(cellValue)) {
            continue;
          }

          rx.lastIndex = 0;

          cell.value = cellValue.replace(rx, (match, key) => {
            const k = String(key).trim();
            const repl = (this.autoFillObject as any)?.[k];
            return repl != null ? String(repl) : match.includes('SIGN') ? match : '';
          });
        }
      }
    }
  }

  getAutoFillColumns(): any[] {
    const autoFillColumns = [] as any[];

    for (const [sheetIndex, sheet] of this.spreadsheetObj.sheets.entries()) {
      if (!sheet?.rows) { continue; }

      for (const [rowIndex, row] of sheet.rows.entries()) {
        if (!row?.cells) { continue; }

        for (const [colIndex, cell] of row.cells.entries()) {
          const value = cell?.value;

          if (
            typeof value === 'string'
            && value.length > 0
            && /\{\{.*?\}\}/.test(value)
            && !cell.formula
          ) {
            autoFillColumns.push({value, sheetIndex, rowIndex, colIndex});
          }
        }
      }
    }
    return autoFillColumns;
  }

  async contextOpen(args: any): Promise<void> {
    if (args.element.parentElement.innerText.includes('Feld einfügen')) {
      this.toggleSubMenuScrollClass(args);
    }

    if (args.element.id === this.spreadsheetObj.element.id + '_contextmenu') {
      await this.spreadsheetObj.getData(
        this.spreadsheetObj.getActiveSheet().name + '!' + this.spreadsheetObj.getActiveSheet().selectedRange
      ).then((res) => {
        let lockaction = 'Sperren';
        const arr = Array.from(res, ([name, value]) => value);
        if (arr.findIndex((cell: any) => cell.isReadOnly
        ) > -1) {
          lockaction = 'Entsperren';
        }
        this.spreadsheetObj.addContextMenuItems([{text: lockaction}], 'Hyperlinks');

        if (!this.readOnly) {
          this.spreadsheetObj.addContextMenuItems([
            {text: 'Zeile hinzufügen'},
            {text: 'Zeile entfernen'},
            {text: 'Rückgängig'}
          ], lockaction);
        }

        if (this.contextMenueItems && this.contextMenueItems.length > 0) {
          this.spreadsheetObj.addContextMenuItems([{text: 'Feld einfügen', items: this.contextMenueItems}], lockaction);
        }
      });
    }
  }

  contextSelect(args: any): void {
    if (args.item.text === 'Zeile hinzufügen') {
      this.insertRowBelowSelected();
      return;
    }
    if (args.item.text === 'Zeile entfernen') {
      this.removeSelectedRow();
      return;
    }
    if (args.item.text === 'Rückgängig') {
      this.undoLastAction();
      return;
    }

    if (this.contextMenueItems) {
      for (const contextMenueItem of this.contextMenueItems) {
        if (contextMenueItem.text.indexOf(args.item.text) > -1) {
          const activeSheet: SheetModel = this.spreadsheetObj.getActiveSheet();
          const activeCell = activeSheet.activeCell;
          if (activeSheet?.activeCell) {
            const activeCellIndexes: number[] = getCellIndexes(activeSheet.activeCell);
            // To get cell model on which context-menu has triggered.
            let cellModel: CellModel = getCell(activeCellIndexes[0], activeCellIndexes[1], activeSheet);

            if (!cellModel) {
              cellModel = {};
            }
            // Update the CellModel value
            cellModel.value = `{{${contextMenueItem.id}}}`;
            // To update value to the cell.
            this.spreadsheetObj.updateCell(cellModel, activeCell);
          }
        }
      }
    }

    if (['Sperren', 'Entsperren'].indexOf(args.item.text) > -1) {
      if (this.spreadsheetObj.getActiveSheet()?.selectedRange === undefined) {
        return;
      }
      this.spreadsheetObj.setRangeReadOnly(
        (args.item.text === 'Sperren')
        , this.spreadsheetObj.getActiveSheet().selectedRange as string
        , this.spreadsheetObj.activeSheetIndex
      );
    }

  }

  async test(args: any): Promise<void> {
    if (['Speichern'].indexOf(args.item.text) > -1) {
      const extractedData = await this.extractData();
      this.spreadsheetObj.saveAsJson().then((json: any) => {
        const jsonWorkbook = json.jsonObject;
        this.Save.emit({jsonWorkbook, extractedData});
      });
    }
  }


  async extractData(): Promise<string> {
    const a = this.spreadsheetObj;
    for (let i = 0; i < a.definedNames.length; i++) {
      const value = a.definedNames[i];
      if (value && value.refersTo) {
        await a.getData(value!.refersTo!.substring(1)).then(async (vv) => {
          const dataIndex = this.Names.findIndex(a => a.key === value.name);
          if (dataIndex === -1) {
            this.Names.push({key: value.name, value: await vv.values()?.next()?.value?.value});
          } else {
            this.Names[dataIndex].value = await vv.values()?.next()?.value?.value;
          }
        });
      }
    }
    return JSON.stringify(this.Names);
  }

  toggleSubMenuScrollClass(args: any): void {
    const subMenuItem = args.element as HTMLElement;
    if (args.parentItem && args.parentItem?.properties.text === 'Feld einfügen') {
      subMenuItem.classList.add('custom-tag-sub-menu');
    } else {
      subMenuItem.classList.remove('custom-tag-sub-menu');
    }
  }

  onCellSelected = async (args: any): Promise<void> => {
    const range = args.range || args.selectedRange;

    if (!range) {
      return;
    }
    const indexes = getCellIndexes(range.split(':')[0]);
    const rowIndex = indexes[0];

    const sheet = this.spreadsheetObj.getActiveSheet();
    const cellAddr = `${sheet.name}!A${rowIndex + 1}`;

    await this.spreadsheetObj.getData(cellAddr).then((res) => {
      const cell = res.values().next().value;
      this.selectedRowIndex = cell?.isReadOnly === true ? null : rowIndex;
    });
  }

  insertRowBelowSelected = async (): Promise<void> => {
    if (this.selectedRowIndex === null) {
      this.modal.showToast(
        'Bitte zuerst eine Zeile auswählen',
        'md-toast app-opacity',
        'fal fa-info-circle');
      return;
    }

    if (this.isInsertingRow) {
      return;
    }

    const sheet = this.spreadsheetObj.getActiveSheet();
    const cellAddr = `${sheet.name}!A${this.selectedRowIndex + 1}`;
    const res = await this.spreadsheetObj.getData(cellAddr);
    const cellCheck = res.values().next().value;

    if (cellCheck?.isLocked && cellCheck?.isLocked !== false) {
      this.modal.showToast(
        'Diese Zeile kann nicht verwendet werden',
        'md-toast app-opacity',
        'fal fa-lock');
      return;
    }

    this.isInsertingRow = true;

    const sourceRowIndex = this.selectedRowIndex;
    const insertAt = sourceRowIndex + 1;
    const sourceRow = sheet?.rows?.[sourceRowIndex];
    const sourceCells = sourceRow?.cells
      ? sourceRow.cells.map((sourceCell: any) => sourceCell
      ? JSON.parse(JSON.stringify(sourceCell)) : null)
      : [];
    const sourceHeight = sourceRow?.height;


    await this.spreadsheetObj.insertRow(insertAt, insertAt);
    await new Promise(resolve => setTimeout(resolve, 300));

    const sheetIndex = this.spreadsheetObj.activeSheetIndex;

    if (sourceHeight) {
      this.spreadsheetObj.setRowHeight(sourceHeight, insertAt, sheetIndex);
    }

    for (let colIdx = 0; colIdx < sourceCells.length; colIdx++) {
      const cell = sourceCells[colIdx];
      if (!cell) {
        continue;
      }
      const newCell: any = {
        style: cell.style ? JSON.parse(JSON.stringify(cell.style)) : undefined,
        format: cell.format,
        isReadOnly: cell.isReadOnly,
        isLocked: cell.isLocked,
        colSpan: cell.colSpan,
        rowSpan: cell.rowSpan,
      };

      if (cell.formula){
        newCell.formula = this.shiftFormulaRow(cell.formula, 1);
      } else {
        newCell.value = '';
      }
      this.spreadsheetObj.updateCell(newCell, `${getColumnHeaderText(colIdx + 1)}${insertAt + 1}`);
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    this.spreadsheetObj.setRangeReadOnly(false, `A${insertAt + 1}:${getColumnHeaderText(50)}${insertAt + 1}`, sheetIndex);
    this.lastAction = {
      type: 'insert',
      rowIndex: insertAt,
      cells: sourceCells,
      height: sourceHeight
    };

    this.isDirty = true;
    this.isInsertingRow = false;
  }

  removeSelectedRow = async (): Promise<void> => {
    const sheet = this.spreadsheetObj.getActiveSheet();
    const activeCell = sheet.activeCell || 'A1';
    const rowIndex = getCellIndexes(activeCell)[0];
    const cellAddr = `${sheet.name}!A${rowIndex + 1}`;

    await this.spreadsheetObj.getData(cellAddr).then((res) => {
      const cell = res.values().next().value;
      if (cell?.isLocked !== false) {
        this.modal.showToast(
          'Diese Zeile kann nicht entfernt werden',
          'md-toast app-opacity',
          'fal fa-lock');
        return;
      }
      const row = sheet?.rows?.[rowIndex];
      this.lastAction = {
        type: 'delete',
        rowIndex,
        cells: row?.cells ? JSON.parse(JSON.stringify(row.cells)) : [],
        height: row?.height
      };
      this.spreadsheetObj.delete(rowIndex, rowIndex, 'Row');
      this.isDirty = true;
    });
  }

  undoLastAction = async (): Promise<void> => {
    if (!this.lastAction) {
      this.modal.showToast(
        'Nichts rückgängig zu machen',
        'md-toast app-opacity',
        'fal fa-info-circle');
      return;
    }

    const { type, rowIndex, cells, height } = this.lastAction;
    this.lastAction = null;

    if (type === 'insert') {
      this.spreadsheetObj.delete(rowIndex, rowIndex, 'Row');
      return;
    }

    await this.spreadsheetObj.insertRow(rowIndex, rowIndex);
    await new Promise(resolve => setTimeout(resolve, 300)); // 250

    const sheetIndex = this.spreadsheetObj.activeSheetIndex;

    if (height) {
      this.spreadsheetObj.setRowHeight(height, rowIndex, sheetIndex);
    }

    for (let colIdx = 0; colIdx < cells.length; colIdx++) {
      const cell = cells[colIdx];
      if (!cell) {
        continue;
      }
      this.spreadsheetObj.updateCell({
        style: cell.style,
        format: cell.format,
        value: cell.value,
        formula: cell.formula,
        isReadOnly: false,
        isLocked: false,
        colSpan: cell.colSpan,
        rowSpan: cell.rowSpan,
      }, `${getColumnHeaderText(colIdx + 1)}${rowIndex + 1}`);
      await new Promise(resolve => setTimeout(resolve, 10));
    }

    this.spreadsheetObj.setRangeReadOnly(false, `A${rowIndex + 1}:${getColumnHeaderText(50)}${rowIndex + 1}`, sheetIndex);
  }

  private shiftFormulaRow(formula: string, shift: number): string {
    return formula.replace(/([A-Z]+)(\d+)/g, (match, col, row) => `${col}${parseInt(row, 10) + shift}`);
  }

  private onNativePaste = (ev: ClipboardEvent) => {
    const sheet = this.spreadsheetObj.getActiveSheet();
    const startAddr = (sheet.selectedRange || 'A1').split(':')[0];
    const [r, c] = getCellIndexes(startAddr);

    const merge = this.findMergedParent(sheet, r, c);
    if (!merge) { return; } // kein Merge -> normaler Paste

    const text = ev.clipboardData?.getData('text/plain');
    if (text == null) { return; }

    // Standard-Paste verhindern, sonst splittet er \n in mehrere Zellen
    ev.preventDefault();
    ev.stopImmediatePropagation();

    const { tlRow, tlCol, rowSpan, colSpan } = merge;

    // 1) Wert (inkl. Zeilenumbrüche) in Top-Left schreiben
    const tlAddr = `${getColumnHeaderText(tlCol + 1)}${tlRow + 1}`;
    // Excel-like line breaks im Zellwert: \n
    const cellText = text.replace(/\r\n/g, '\n');
    this.spreadsheetObj.updateCell({ value: cellText }, tlAddr);

    // 2) Merge wieder herstellen (falls er durch vorherige Aktionen verloren ging)
    const endAddr = `${getColumnHeaderText(tlCol + colSpan)}${tlRow + rowSpan}`;
    this.spreadsheetObj.merge(`${tlAddr}:${endAddr}`);

    // 3) Wrap aktivieren, damit mehrzeilig sichtbar ist
    this.spreadsheetObj.wrap(tlAddr, true);
  }

  private findMergedParent(sheet: any, r: number, c: number):
    | { tlRow: number; tlCol: number; rowSpan: number; colSpan: number }
    | null {

    // Direkt Top-Left?
    const direct = sheet?.rows?.[r]?.cells?.[c];
    if (direct && ((direct.rowSpan ?? 1) > 1 || (direct.colSpan ?? 1) > 1)) {
      return { tlRow: r, tlCol: c, rowSpan: direct.rowSpan ?? 1, colSpan: direct.colSpan ?? 1 };
    }

    // Scan nach Top-Left, dessen Span (r,c) abdeckt
    for (let rr = r; rr >= 0; rr--) {
      const row = sheet?.rows?.[rr];
      if (!row?.cells) { continue; }

      for (let cc = c; cc >= 0; cc--) {
        const cell = row.cells?.[cc];
        if (!cell) { continue; }

        const rs = cell.rowSpan ?? 1;
        const cs = cell.colSpan ?? 1;
        const isMergeTL = rs > 1 || cs > 1;
        if (!isMergeTL) { continue; }

        const covers = r >= rr && r <= rr + rs - 1 && c >= cc && c <= cc + cs - 1;
        if (covers) { return { tlRow: rr, tlCol: cc, rowSpan: rs, colSpan: cs }; }
      }
    }
    return null;
  }

  setCellValueByIndex(
    sheetIndex: number,
    rowIndex: number,
    colIndex: number,
    value: string
  ): void {
    const sheet = this.spreadsheetObj.sheets?.[sheetIndex];
    if (!sheet?.name) { return; }

    // Aktuellen Zellwert auslesen
    const cell = getCell(rowIndex, colIndex, sheet);
    const currentValue = cell?.value != null ? String(cell.value) : '';

    // Nur updaten wenn sich der Wert unterscheidet
    if (currentValue === value) { return; }

    if (!this.isDirty) {
      this.isDirty = true;
    }

    const address = `${sheet.name}!${getColumnHeaderText(colIndex + 1)}${rowIndex + 1}`;
    this.spreadsheetObj.updateCell({ value }, address);
  }

  normalizeSpreadsheetFontSizes(): Promise<void> {
    return new Promise((resolve) => {
      const defaultFontSize = 6;
      const maxFontSize = 400;

      for (const sheet of this.spreadsheetObj?.sheets || []) {
        for (const row of sheet?.rows || []) {
          for (const cell of row?.cells || []) {
            if (!cell?.style?.fontSize) {
              continue;
            }

            const fontSize = cell.style.fontSize;

            // Unterstützt beispielsweise "10pt", "10px" oder numerische Werte.
            const parsedFontSize =
              typeof fontSize === 'number'
                ? fontSize
                : Number.parseFloat(String(fontSize));

            if (
              !Number.isFinite(parsedFontSize) ||
              parsedFontSize <= 0 ||
              parsedFontSize > maxFontSize
            ) {
              cell.style.fontSize = `${defaultFontSize}pt`;
            }
          }
        }
      }

      resolve();
    });
  }

  normalizeSpreadsheetColors(): Promise<void> {
    return new Promise((resolve) => {
      for (const sheet of this.spreadsheetObj?.sheets || []) {
        for (const row of sheet?.rows || []) {
          for (const cell of row?.cells || []) {
            if (!cell?.style) {
              continue;
            }

            if (cell.style.backgroundColor) {
              cell.style.backgroundColor = this.rgbToHex(cell.style.backgroundColor);
            }

            if (cell.style.color) {
              cell.style.color = this.rgbToHex(cell.style.color);
            }

            if (cell.style.border) {
              cell.style.border = this.replaceRgbInBorder(cell.style.border);
            }

            if (cell.style.borderTop) {
              cell.style.borderTop = this.replaceRgbInBorder(cell.style.borderTop);
            }

            if (cell.style.borderRight) {
              cell.style.borderRight = this.replaceRgbInBorder(cell.style.borderRight);
            }

            if (cell.style.borderBottom) {
              cell.style.borderBottom = this.replaceRgbInBorder(cell.style.borderBottom);
            }

            if (cell.style.borderLeft) {
              cell.style.borderLeft = this.replaceRgbInBorder(cell.style.borderLeft);
            }
          }
        }
      }

      resolve();
    });
  }

  replaceRgbInBorder(border: string): string {
    if (!border) {
      return border;
    }

    return border.replace(
      /rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)/gi,
      (_, r, g, b) => this.rgbToHex(`rgb(${r}, ${g}, ${b})`)
    );
  }

  rgbToHex(color: string): string {
    if (!color) {
      return color;
    }

    // Bereits Hex oder Name
    if (color.startsWith('#') || /^[a-zA-Z]+$/.test(color)) {
      return color;
    }

    const match = color.match(/^rgb\s*\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    if (!match) {
      return color;
    }

    if (!this.isDirty) {
      this.isDirty = true;
    }

    const r = Math.max(0, Math.min(255, Number(match[1])));
    const g = Math.max(0, Math.min(255, Number(match[2])));
    const b = Math.max(0, Math.min(255, Number(match[3])));

    return '#' + [r, g, b]
      .map(v => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  }
}
